import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../hooks/useCurrency';
import { registraBarra, togliBarra } from '../utils/barreFisse';
import { prezzoTesto } from '../utils/prezzo';
import { usePricing } from '../hooks/usePricing';
import { useStartCheckout2TB } from '../hooks/useStartCheckout2TB';
import { useContoOfferta } from '../hooks/useContoOfferta';
import InAppBrowserWarning from './InAppBrowserWarning';

// IL BANNER DELL'OFFERTA — un componente solo per tutti i posti
// (2026-09-07, rifatto in giornata).
//
// COSA E' CAMBIATO NELLA RISCRITTURA, e perche':
//
// 1. LEGGIBILITA'. Prima era testo quasi nero (#04140a) su un verde chiaro
//    (#22c55e): sul tema chiaro spariva. Ora il fondo e' verde SCURO e il
//    testo bianco — 6,28:1 nel punto piu' chiaro del gradiente, calcolato,
//    non stimato a occhio. Il conto e il bottone sono riquadri bianchi con
//    testo verde scuro: 9,34:1. La soglia chiesta era 4,5:1.
//    I colori sono scritti a mano e NON presi dai token del tema, di
//    proposito: la barra deve leggersi identica in chiaro e in scuro, e un
//    token che cambia col tema e' esattamente cio' che l'ha rotta prima.
//
// 2. SCADENZA A 24 ORE, da localStorage, che riparte quando arriva a zero
//    (hooks/useContoOfferta.js). E' la scelta dell'operatore per il test.
//    Perche' allora regge: le DATE scritte altrove nella pagina sono state
//    nascoste (vedi App.css, blocco "TEST BANNER"), quindi il conto e'
//    l'unica scadenza visibile e non contraddice piu' niente.
//
// `posto` decide solo la forma:
//   'barra' -> striscia a tutta larghezza, fissa in cima (home desktop, vault)
//   'card'  -> il riquadro in basso su telefono, dov'era gia'
export default function BannerOfferta({ posto = 'barra', onRegisterBuyIntent, soloSeGratuito = false, pianoUtente = null, nonCliccabile = false }) {
  const { t, i18n } = useTranslation('home');
  const { currency } = useCurrency();
  const { priceOf, posti, prezzo2tbDopo } = usePricing(currency);
  // Timer a 24 ore da localStorage, che riparte a zero: e' la scelta
  // dell'operatore per questo test. Il gancio torna la sola stringa.
  const conto = useContoOfferta();
  const { buy2tb, loading, inAppWarningProps } = useStartCheckout2TB('promo_bar', 'lifetime_2tb');

  const prezzo = priceOf('lifetime_2tb', currency);

  // LO SPAZIO SOTTO LA PAGINA (2026-09-08).
  //
  // La card e' position:fixed, quindi non occupa spazio nel flusso: la pagina
  // finisce SOTTO di lei. Misurato a 390px: scorrendo in fondo alla card del
  // 2 TB il pulsante d'acquisto restava coperto — elementFromPoint sul suo
  // centro tornava il banner, non il bottone, cioe' il clic finiva altrove.
  // Su un banner che vende, coprire il pulsante che vende e' il difetto
  // peggiore possibile.
  //
  // L'altezza NON e' una costante: il testo va su due righe quando la valuta
  // e' piu' lunga, i posti compaiono e spariscono, e i telefoni hanno
  // barre di navigazione di altezze diverse. Si misura quella vera e la si
  // pubblica come variabile CSS, che App.css usa come padding in fondo.
  //
  // Si misura dalla CIMA della card fino al fondo della finestra, non la sola
  // altezza: cosi' il numero comprende gia' lo stacco dal bordo e la
  // safe-area del telefono, senza doverli ripetere qui e in App.css — due
  // posti da tenere allineati a mano sono due posti che si disallineano.
  const riferimento = useRef(null);
  // LA FORMA ETICHETTA DENTRO L'APP (2026-09-14): una riga sola, e se la
  // frase intera non ci sta si toglie "invece di 99" PER INTERO. Un prezzo
  // non finisce mai nei puntini: o c'e' tutto o non c'e'. La misura e' dal
  // vivo: un secondo span nascosto porta sempre la frase intera, e si
  // confronta la sua larghezza con quella della striscia, a ogni cambio di
  // dimensione (rotazione compresa) — cosi' torna lunga se lo spazio torna.
  const misuraRef = useRef(null);
  const [corta, setCorta] = useState(false);
  useEffect(() => {
    if (!nonCliccabile) return undefined;
    const barra = riferimento.current, misura = misuraRef.current;
    if (!barra || !misura) return undefined;
    const controlla = () => {
      const disponibile = barra.clientWidth - 20; // il padding ai lati
      setCorta(misura.scrollWidth > disponibile);
    };
    controlla();
    const ro = new ResizeObserver(controlla);
    ro.observe(barra);
    return () => ro.disconnect();
  }, [nonCliccabile, prezzo?.amount, prezzo2tbDopo]);
  useEffect(() => {
    // LA BARRA PUBBLICA LA PROPRIA ALTEZZA (2026-09-10).
    //
    // App.css usava gia' --banner-h in tre punti — l'altezza minima della
    // barra, il padding del guscio del vault e il `top` della sua
    // intestazione — ma NESSUNO la impostava: erano regole morte. Il
    // risultato, misurato a 390px: la barra alta 127px stava sopra
    // l'intestazione del vault, che cominciava a 72 e restava coperta per
    // 55px. Qui si misura quella vera e la si pubblica, cosi' quelle tre
    // regole cominciano a funzionare senza toccarle.
    const radice = document.documentElement;
    if (posto === 'barra') {
      const nodo = riferimento.current;
      if (!nodo) { radice.style.removeProperty('--banner-h'); return undefined; }
      // E LA PUBBLICA ANCHE AL REGISTRO DELLE BARRE (2026-09-13).
      //
      // --banner-h resta: la usano la home e le pagine pubbliche. Ma nel vault
      // le barre fisse sono diventate tre, e ognuna che si calcolava lo spazio
      // per conto suo significava che due si coprivano — questa striscia
      // finiva sotto il banner dell'avviso. Adesso dice la sua altezza al
      // registro, che decide dove comincia ognuna e di quanto scende la
      // pagina.
      const misura = () => {
        const h = Math.ceil(nodo.getBoundingClientRect().height);
        radice.style.setProperty('--banner-h', h + 'px');
        registraBarra('offerta', h);
      };
      misura();
      const ro = new ResizeObserver(misura);
      ro.observe(nodo);
      window.addEventListener('resize', misura);
      window.addEventListener('orientationchange', misura);
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', misura);
        window.removeEventListener('orientationchange', misura);
        radice.style.removeProperty('--banner-h');
        togliBarra('offerta');
      };
    }
    if (posto === 'fondo') {
      // La striscia agganciata in fondo pubblica la propria altezza vera:
      // la lista dei file (.vz-main) le somma un padding-bottom cosi' l'ultimo
      // file non finisce nascosto dietro di lei. Misurata dal vivo perche'
      // cresce quando il testo va a capo sotto i 430px.
      const nodo = riferimento.current;
      if (!nodo) { radice.style.removeProperty('--vz-offerta-fondo-h'); return undefined; }
      const misura = () => radice.style.setProperty('--vz-offerta-fondo-h', Math.ceil(nodo.getBoundingClientRect().height) + 'px');
      misura();
      const ro = new ResizeObserver(misura);
      ro.observe(nodo);
      window.addEventListener('resize', misura);
      window.addEventListener('orientationchange', misura);
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', misura);
        window.removeEventListener('orientationchange', misura);
        radice.style.removeProperty('--vz-offerta-fondo-h');
      };
    }
    if (posto !== 'card') return undefined;
    const nodo = riferimento.current;
    // Il banner puo' sparire anche restando montato (prezzo non ancora
    // arrivato, utente gia' pagante): senza questo, lo spazio riservato
    // resterebbe come un vuoto in fondo alla pagina.
    if (!nodo) { radice.style.removeProperty('--ltc-spazio-banner'); return undefined; }
    const misura = () => {
      const r = nodo.getBoundingClientRect();
      // SOPRA I 700px LA CARD NON C'E' (2026-09-14). Il suo contenitore
      // (.ltc-banner-mobile) e' display:none su desktop: il rettangolo e'
      // tutto a zero e "innerHeight - top" darebbe quasi uno schermo intero di
      // padding fantasma sotto il footer (misurato: 908px su ogni pagina che
      // monta la home). Se la card non e' disegnata, non si riserva niente.
      if (r.width === 0 && r.height === 0) { radice.style.removeProperty('--ltc-spazio-banner'); return; }
      const spazio = Math.ceil(window.innerHeight - r.top) + 8; // +8: un filo d'aria
      radice.style.setProperty('--ltc-spazio-banner', spazio + 'px');
    };
    misura();
    // ResizeObserver per il testo che va a capo; resize/orientationchange per
    // la rotazione e per le barre del browser che compaiono e scompaiono.
    const ro = new ResizeObserver(misura);
    ro.observe(nodo);
    window.addEventListener('resize', misura);
    window.addEventListener('orientationchange', misura);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', misura);
      window.removeEventListener('orientationchange', misura);
      // Alla scomparsa del banner lo spazio va restituito, altrimenti resta
      // un vuoto in fondo alla pagina per sempre.
      radice.style.removeProperty('--ltc-spazio-banner');
    };
    // Le dipendenze sono quelle che decidono se la card viene DISEGNATA:
    // al primo render il prezzo non e' ancora arrivato, il componente torna
    // null e il riferimento e' vuoto. Senza queste, l'effetto non si
    // ripeteva quando la card compariva e lo spazio restava a zero — che e'
    // esattamente il difetto che questo blocco doveva risolvere.
  }, [posto, prezzo?.amount, soloSeGratuito, pianoUtente]);
  if (soloSeGratuito && pianoUtente && pianoUtente !== 'free') return null;
  // Senza prezzo non si disegna: meglio nessuna barra che "2TB a undefined".
  if (!prezzo?.amount) return null;

  const cifra = prezzoTesto(prezzo.symbol, prezzo.amount, i18n.language);
  // IL 99 BARRATO ACCANTO AL 79 (2026-09-11). Il prezzo pieno viene dalla
  // stessa tabella del server che decide cosa si paga (lifetime_2tb_dopo),
  // per valuta: 99 EUR, 109 USD, 89 GBP. Mai scritto a mano. Se il server
  // non lo manda, la riga resta "2TB a vita a 79" come prima.
  const cifraPiena = prezzo2tbDopo != null ? prezzoTesto(prezzo.symbol, prezzo2tbDopo, i18n.language) : null;
  const testoPrezzo = cifraPiena ? (
    <>
      {t('banner_lampo_corpo_prima')}{' '}
      {/* UN SOLO CORPO, NESSUNA BARRATURA (2026-09-12). Il 99 barrato e' stato
          tolto: con due prezzi accostati l'occhio vedeva una macchia sola, e
          rimpicciolire il 99 per distinguerlo lo rendeva illeggibile (provato
          su schermo la sera prima). Adesso lo dice una frase: "79 invece di
          99", tutto alla stessa misura; a distinguere sono il peso e
          l'opacita', non la dimensione. */}
      <span className="ltc-banner-coppia">
        <span className="ltc-banner-prezzo">{cifra}</span>{' '}
        <span className="ltc-banner-invece">{t('banner_lampo_invece')} {cifraPiena}</span>
      </span>
    </>
  ) : t('banner_lampo_corpo', { price: cifra });
  // I POSTI DELL'OFFERTA (2026-09-07). Il banner vende il 2 TB, quindi mostra
  // i posti del 2 TB — non un totale di tutti i piani, che qui non
  // significherebbe niente. Se quel piano non e' a numero chiuso non si
  // disegna nulla: il banner resta come prima.
  const postiOfferta = posti?.lifetime_2tb || null;

  function acquista() {
    if (onRegisterBuyIntent) return onRegisterBuyIntent({ plan: 'lifetime_2tb', campaign: 'promo_bar' }, 'banner_lampo');
    return buy2tb();
  }

  // Il conto e il bottone: identici nelle due forme, quindi scritti una volta.
  const orologio = (
    <span className="ltc-banner-conto" aria-label={`${t('banner_lampo_scade')} ${conto}`}>{conto}</span>
  );
  // Riquadro chiaro come il conto, cosi' i due numeri che contano — quanto
  // tempo resta e quanti posti restano — si leggono alla stessa maniera.
  const pillolaPosti = postiOfferta && (
    <span className="ltc-banner-posti">
      {postiOfferta.rimasti === 0
        ? t('banner_lampo_posti_zero')
        : t('banner_lampo_posti', { count: postiOfferta.rimasti })}
    </span>
  );
  const bottone = (
    nonCliccabile ? (
      // DENTRO L'APP il prezzo si legge ma non si preme: un percorso che porta
      // a pagare fuori dal sistema di Google, dentro un'app, non e' ammesso.
      // Si toglie l'azione, non l'informazione.
      <span className="ltc-banner-cta ltc-banner-cta-spenta">{t('banner_lampo_cta')}</span>
    ) : (
      <button type="button" className="ltc-banner-cta" onClick={acquista} disabled={loading}>
        {loading ? '…' : t('banner_lampo_cta')}
      </button>
    )
  );

  if (posto === 'fondo') {
    // La striscia agganciata in fondo al file manager (2026-09-16): stesso
    // contenuto dinamico della fascia in alto (etichetta, prezzo/invece,
    // conto, posti, "Compra ora"), solo spostato e vestito di verde pieno.
    // La visibilita' fuori dall'app la decide VaultPage con !dentroApp().
    return (
      <div className="ltc-banner-fondo" ref={riferimento} role="region" aria-label={t('banner_lampo_aria')}>
        <span className="ltc-banner-etichetta">{t('banner_lampo_etichetta')}</span>
        <span className="ltc-banner-testo">{testoPrezzo}</span>
        <div className="ltc-banner-fondo-riga2">
          <span className="ltc-banner-scade">{t('banner_lampo_scade')}</span>
          {orologio}
          {pillolaPosti}
          {bottone}
        </div>
        {inAppWarningProps && <InAppBrowserWarning {...inAppWarningProps} />}
      </div>
    );
  }

  if (posto === 'card') {
    return (
      <div className="ltc-banner-card" ref={riferimento} role="region" aria-label={t('banner_lampo_aria')}>
        {/* Due righe su telefono: il testo sopra, conto e bottone sotto.
            In una riga sola i quattro pezzi si comprimerebbero fino a
            spezzare le parole. */}
        {/* I POSTI STANNO SULLA PRIMA RIGA (2026-09-07). In fondo alla
            seconda, insieme a "scade tra", al conto e al bottone, non ci
            stavano: a 360px il bottone finiva tagliato dall'overflow:hidden
            della card. Sopra invece c'e' spazio, e stanno accanto al prezzo
            che e' il numero a cui si riferiscono. */}
        <div className="ltc-banner-card-riga1">
          <span className="ltc-banner-etichetta">{t('banner_lampo_etichetta')}</span>
          <span className="ltc-banner-testo">{testoPrezzo}</span>
          {pillolaPosti}
        </div>
        <div className="ltc-banner-card-riga2">
          <span className="ltc-banner-scade">{t('banner_lampo_scade')}</span>
          {orologio}
          {bottone}
        </div>
        {inAppWarningProps && <InAppBrowserWarning {...inAppWarningProps} />}
      </div>
    );
  }

  if (nonCliccabile) {
    // DENTRO L'APP LA STRISCIA E' UN'ETICHETTA (2026-09-14). Nessun tasto,
    // nessun onClick, nessun ruolo interattivo: un percorso che porta a pagare
    // fuori dal sistema di Google, dentro un'app, non e' ammesso — e un tasto
    // che non porta da nessuna parte e' una presa in giro. Resta
    // l'informazione: l'offerta, il prezzo, e dove si trova.
    const pezzoInvece = cifraPiena ? ` ${t('banner_lampo_invece')} ${cifraPiena}` : '';
    const intera = `${t('banner_lampo_etichetta')} · ${t('banner_lampo_corpo_prima')} ${cifra}${pezzoInvece} · lifetimecloud.me`;
    const visibile = corta
      ? `${t('banner_lampo_etichetta')} · ${t('banner_lampo_corpo_prima')} ${cifra} · lifetimecloud.me`
      : intera;
    return (
      <div className="ltc-banner-barra ltc-banner-app" ref={riferimento} role="note" aria-label={t('banner_lampo_aria')}>
        <span className="ltc-banner-app-testo">{visibile}</span>
        <span className="ltc-banner-app-misura" aria-hidden="true" ref={misuraRef}>{intera}</span>
      </div>
    );
  }

  return (
    <div className="ltc-banner-barra" ref={riferimento} role="region" aria-label={t('banner_lampo_aria')}>
      {/* DUE RIGHE VERE, NON PAROLE TOLTE (2026-09-11).
          Prima i sei pezzi erano fratelli in una riga sola: a 390px non ci
          stavano, e il rimedio era stato nascondere l'etichetta, "scade tra" e
          i posti, poi accorciare la frase. Cioe' togliere informazione per
          fare spazio.
          Adesso i pezzi stanno in due gruppi. Su schermo largo i due gruppi
          sono `display:contents`: spariscono dal layout e i sei pezzi tornano
          fratelli in una riga sola, identica a oggi. Sotto i 430px diventano
          due righe compatte e si vede TUTTO:
            riga 1  OFFERTA LAMPO · 2TB a vita a EUR79
            riga 2  scade tra · 22:50:30 · 4 posti · Compra ora */}
      <div className="ltc-banner-barra-corpo">
        <span className="ltc-banner-riga ltc-banner-riga1">
          <span className="ltc-banner-etichetta">{t('banner_lampo_etichetta')}</span>
          <span className="ltc-banner-testo">{testoPrezzo}</span>
        </span>
        <span className="ltc-banner-riga ltc-banner-riga2">
          <span className="ltc-banner-scade">{t('banner_lampo_scade')}</span>
          {orologio}
          {pillolaPosti}
          {bottone}
        </span>
      </div>
      {inAppWarningProps && <InAppBrowserWarning {...inAppWarningProps} />}
    </div>
  );
}
